import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import DataGridTypes from 'types/DataGridTypes';
import { useNurturingCandidatePreferences } from './useNurturingCandidatePreferences';
import RunLogsApiService, {
  getNurturingRowKey,
  PickDateFilterType,
  NurturingPreferencesResponse,
} from '../apiService/runLogsApiService';

export function usePickCandidateConfirmation(
  dealerId: string,
  onPickSuccess?: () => void,
  subscribeToJob?: (jobId: string) => void,
) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = React.useState(false);
  const [pickLoading, setPickLoading] = React.useState(false);
  const [dateFilterType, setDateFilterType] = React.useState<Exclude<PickDateFilterType, 'ALL'> | null>(null);
  const [startDate, setStartDate] = React.useState<string | null>(null);
  const [endDate, setEndDate] = React.useState<string | null>(null);
  const [isAllChecked, setIsAllChecked] = React.useState(false);
  const [batchSizesBeforeAll, setBatchSizesBeforeAll] = React.useState<Record<string, number> | null>(null);

  const preferences = useNurturingCandidatePreferences(dealerId, isOpen);

  // Track whether user manually changed filter controls before prefill completes.
  const hasUserTouchedFilterRef = React.useRef(false);
  const didPrefillDateFilterRef = React.useRef(false);
  React.useEffect(() => {
    if (!isOpen) {
      didPrefillDateFilterRef.current = false;
      hasUserTouchedFilterRef.current = false;
      return;
    }
    if (didPrefillDateFilterRef.current) return;
    if (preferences.loading) return;
    // Wait for an actual API outcome before prefill (success or error),
    // otherwise initial empty state can get locked in before fetch starts.
    if (!preferences.rawResponse && !preferences.error) return;
    if (hasUserTouchedFilterRef.current) return;

    // preferences.dateFilterType/startDate/endDate are already normalized by the preferences hook
    didPrefillDateFilterRef.current = true;
    setDateFilterType(preferences.dateFilterType ?? null);
    setStartDate(preferences.startDate ?? null);
    setEndDate(preferences.dateFilterType === 'DATE_RANGE' ? (preferences.endDate ?? null) : null);
    setIsAllChecked(Boolean(preferences.isAllChecked));
    setBatchSizesBeforeAll(null);
  }, [
    isOpen,
    preferences.loading,
    preferences.rawResponse,
    preferences.error,
    preferences.dateFilterType,
    preferences.startDate,
    preferences.endDate,
    preferences.isAllChecked,
  ]);

  const handleOpen = React.useCallback(() => {
    setDateFilterType(null);
    setStartDate(null);
    setEndDate(null);
    setIsAllChecked(false);
    setBatchSizesBeforeAll(null);
    setIsOpen(true);
  }, []);

  const handleClose = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleDateFilterTypeChange = React.useCallback((nextType: Exclude<PickDateFilterType, 'ALL'> | null) => {
    hasUserTouchedFilterRef.current = true;
    setDateFilterType(nextType);
    if (nextType === 'SINCE_DATE') {
      setEndDate(null);
    }
  }, []);

  const handleStartDateChange = React.useCallback((nextDate: string | null) => {
    hasUserTouchedFilterRef.current = true;
    setStartDate(nextDate);
  }, []);

  const handleEndDateChange = React.useCallback((nextDate: string | null) => {
    hasUserTouchedFilterRef.current = true;
    setEndDate(nextDate);
  }, []);

  const handleAllCheckedChange = React.useCallback(
    (checked: boolean) => {
      hasUserTouchedFilterRef.current = true;
      if (checked) {
        setBatchSizesBeforeAll({ ...preferences.batchSizeByRowKey });
        preferences.rows.forEach(row => {
          preferences.updateBatchSize(
            getNurturingRowKey(row),
            Math.max(0, row.available_for_nurturing_candidate ?? 0),
          );
        });
        setIsAllChecked(true);
        return;
      }

      const restoreMap = batchSizesBeforeAll;
      if (restoreMap) {
        Object.entries(restoreMap).forEach(([rowKey, value]) => {
          preferences.updateBatchSize(rowKey, value);
        });
      }
      setIsAllChecked(false);
      setBatchSizesBeforeAll(null);
    },
    [preferences.batchSizeByRowKey, preferences.rows, preferences.updateBatchSize, batchSizesBeforeAll],
  );

  const handleConfirmPick = React.useCallback(async () => {
    if (!dealerId) return;
    if (dateFilterType === 'SINCE_DATE' && !startDate) {
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: 'Please select start date',
        }),
      );
      return;
    }
    if (dateFilterType === 'DATE_RANGE' && (!startDate || !endDate)) {
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: 'Please select both start and end date',
        }),
      );
      return;
    }
    if (
      dateFilterType === 'DATE_RANGE' &&
      startDate &&
      endDate &&
      new Date(startDate).getTime() > new Date(endDate).getTime()
    ) {
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: 'Start date cannot be after end date',
        }),
      );
      return;
    }
    setPickLoading(true);
    try {
      // Build the full preferences payload: raw API response with edited batch sizes merged in
      const payloadPreferences: NurturingPreferencesResponse | undefined =
        preferences.rawResponse
          ? {
              ...preferences.rawResponse,
              properties: {
                ...preferences.rawResponse.properties,
                row: preferences.rows.map(row => ({
                  ...row,
                  batch_size: preferences.batchSizeByRowKey[getNurturingRowKey(row)] ?? row.batch_size,
                })),
                // Persist only snake_case filter fields in preferences payload.
                ...(function () {
                  const normalizedType = dateFilterType ?? null;
                  const normalizedStart = dateFilterType ? startDate ?? null : null;
                  const normalizedEnd = dateFilterType === 'DATE_RANGE' ? endDate ?? null : null;
                  return {
                    all: isAllChecked,
                    date_filter_type: normalizedType,
                    start_date: normalizedStart,
                    end_date: normalizedEnd,
                  } as any;
                })(),
              },
            }
          : undefined;

      const result = await RunLogsApiService.pickNurturingCandidates(dealerId, {
        preferences: payloadPreferences,
        startDate:
          dateFilterType === 'SINCE_DATE' || dateFilterType === 'DATE_RANGE'
            ? (startDate ?? undefined)
            : undefined,
        endDate: dateFilterType === 'DATE_RANGE' ? (endDate ?? undefined) : undefined,
      });

      const jobId = (result as { jobId?: string })?.jobId;
      if (jobId && subscribeToJob) {
        subscribeToJob(jobId);
      }

      queryClient.removeQueries({
        queryKey: ['GET', `${DataGridTypes.API_VERSION_2}/run-v2`],
        exact: false,
      });

      onPickSuccess?.();
      setIsOpen(false);

      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'success',
          message: 'Nurturing candidate picker job triggered successfully',
        }),
      );
    } catch (err: any) {
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: err?.message || 'Failed to trigger nurturing candidate picker',
        }),
      );
    } finally {
      setPickLoading(false);
    }
  }, [
    dealerId,
    dateFilterType,
    isAllChecked,
    startDate,
    endDate,
    preferences.rawResponse,
    preferences.rows,
    preferences.batchSizeByRowKey,
    dispatch,
    queryClient,
    onPickSuccess,
    subscribeToJob,
  ]);

  return {
    isOpen,
    handleOpen,
    handleClose,
    handleConfirmPick,
    pickLoading,
    preferencesLoading: preferences.loading,
    preferencesRefreshLoading: preferences.refreshLoading,
    preferencesError: preferences.error,
    rows: preferences.rows,
    headerRow: preferences.headerRow,
    ttl: preferences.ttl,
    soldWaitDays: preferences.soldWaitDays,
    unsoldWaitDays: preferences.unsoldWaitDays,
    batchSizeByRowKey: preferences.batchSizeByRowKey,
    updateBatchSize: preferences.updateBatchSize,
    refreshPreferences: preferences.refreshData,
    dateFilterType,
    setDateFilterType: handleDateFilterTypeChange,
    startDate,
    setStartDate: handleStartDateChange,
    endDate,
    setEndDate: handleEndDateChange,
    isAllChecked,
    onAllCheckedChange: handleAllCheckedChange,
  };
}
